<template>
    <div id="chat-history" class="mt-5 px-2" style="max-height: 100vh; overflow: scroll">
        <div class="d-flex justify-content-between align-items-baseline" style="border-bottom: 1px solid white">
            <a-button-group v-if="!!selectedConversationId" style="position: sticky; top: 0; z-index: 9999">
                <a-button ghost @click="selectedConversationId = undefined">
                    <a-icon type="left" />
                    {{ $t("message.goback") }}
                </a-button>
            </a-button-group>
            <div v-if="!selectedConversationId" class="d-flex justify-content-between w-100">
                <h4>{{ $t("message.chatHistory") }}</h4>

                <a-button ghost type="primary" @click="$emit('toggle-chat-history', true)">
                    {{ $t("message.close") }}
                    <a-icon type="close" />
                </a-button>
            </div>

            <h3 v-else style="font-size: 10px">{{ getTitle() }}</h3>
        </div>

        <div v-if="!!selectedConversationId" style="position: fixed; top: 90px; bottom: 30px; width: 100%">
            <vue-scroll id="vueScroll" ref="vs" :ops="vuescrollOptions">
                <div style="padding-right: 10px; padding-bottom: 10px">
                    <MessageList
                        :alwaysScrollToBottom="true"
                        :colors="colors"
                        :messageStyling="messageStyling"
                        :messages="messages"
                        :participants="participants"
                        :scrollVS="$refs['vs']"
                        :showTypingIndicator="''"
                        :conversation-terminated="true"
                    >
                        <template v-slot:user-avatar="scopedProps">
                            <slot :message="scopedProps.message" :user="scopedProps.user" name="user-avatar"></slot>
                        </template>
                        <template v-slot:text-message-body="scopedProps">
                            <slot
                                :me="scopedProps.me"
                                :message="scopedProps.message"
                                :messageColors="scopedProps.messageColors"
                                :messageText="scopedProps.messageText"
                                name="text-message-body"
                            >
                            </slot>
                        </template>
                        <template v-slot:system-message-body="scopedProps">
                            <slot :message="scopedProps.message" name="system-message-body"></slot>
                        </template>
                        <template v-slot:text-message-toolbox="scopedProps">
                            <slot :me="scopedProps.me" :message="scopedProps.message" name="text-message-toolbox">
                            </slot>
                        </template>
                    </MessageList>
                </div>
            </vue-scroll>
        </div>

        <div
            v-if="!selectedConversationId"
            id="history-list"
            style="position: fixed; top: 90px; bottom: 30px; width: 100%"
        >
            <vue-scroll>
                <div v-if="loading">
                    <a-skeleton v-for="a of [1, 2, 3]" :key="a" :paragraph="{ rows: 2 }" active avatar />
                </div>

                <div v-for="chat of chats" v-else class="item" @click="() => selectChat(chat)">
                    <div>
                        <a-avatar
                            :size="50"
                            :src="chat.users.length ? chat.users[0].gravatar : undefined"
                            :style="{ backgroundColor: '#4E8CFF' }"
                            :username="chat.users.length ? chat.users[0].name : chat.fullname"
                            class="avatarIcon"
                        >
                            <div>
                                {{
                                    ((chat.users.length ? chat.users[0].name : chat.fullname) || "*")
                                        .substr(0, 1)
                                        .toUpperCase()
                                }}
                            </div>
                        </a-avatar>
                    </div>
                    <div class="content w-100">
                        <div class="d-flex justify-content-between w-100">
                            <span class="participant">{{ getParticipants(chat) }}</span>
                            <span class="time">{{ getDate(chat) }}</span>
                        </div>
                        <div class="d-flex flex-column messages">
                            <span
                                v-if="getFirstMessage(chat)"
                                v-html="messageText(getFirstMessage(chat).description)"
                            ></span>
                            <p v-if="getLastMessage(chat)" v-html="messageText(getLastMessage(chat).description)"></p>
                        </div>
                    </div>
                    <div></div>
                </div>
                <div class="d-flex w-100 justify-content-center mt-2">
                    <a-button
                        v-if="loadMore && !(!loading && chats.length === 0)"
                        :disabled="loading"
                        :loading="loading"
                        ghost
                        @click="page += 1"
                    >
                        {{ loading ? $t("message.loading") : $t("message.loadMore") }}
                    </a-button>
                </div>
            </vue-scroll>
            <a-result v-if="!loading && chats.length === 0" :title="$t('message.noHistory')" class="text-light">
                <template #icon>
                    <a-icon theme="twoTone" type="folder-open" />
                </template>
                <template #extra>
                    <a-button type="primary" @click="$emit('toggle-chat-history', true)">
                        {{ $t("message.close") }}
                    </a-button>
                </template>
            </a-result>
        </div>
    </div>
</template>
<script>
import { fetchChatHistory } from "../../api/visitor";
import { mapState } from "vuex";
import log2 from "../../utils/log2";
import { htmlUnescape } from "escape-goat";
import Autolinker from "autolinker";
import request from "../../utils/request";
import * as _ from "lodash";
import MessageList from "./popup-chat/MessageList";
import  DateUtils  from "../../utils/DateUtil";

const log = log2("ChatHistoryList");
export default {
    components: {
        MessageList,
    },
    props: {
        vuescrollOptions: {
            type: Object,
        },
        colors: {
            type: Object,
        },
        messageStyling: {
            type: Boolean,
        },
    },
    data() {
        return {
            selectedConversationId: undefined,
            chats: [],
            loading: true,
            messages: [],
            participants: [],
            page: 1,
            loadMore: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            visitorId: (state) => state.chat.visitorId,
        }),
    },
    methods: {
        getDate(c) {
            return DateUtils.timeFromNow(c.createdDate);
        },
        getTitle() {
            const c = this.chats.find((x) => x._id === this.selectedConversationId);
            return `${this.$t("message.chatDate")} ` + DateUtils.formatDateTime(c.createdDate, this.$i18n.locale);
        },
        getParticipants(c) {
            if (c.users.length === 0) {
                return this.$t("message.you");
            } else if (c.users.length === 1) {
                return `${this.$t("message.you")} ${this.$t("message.and")} ${_.startCase(
                    _.head(c.users).name.trim().toLowerCase()
                )}`;
            } else {
                return [this.$t("message.you"), ...c.users.map((x) => _.startCase(x.name.trim().toLowerCase()))].join(
                    ","
                );
            }
        },
        getFirstMessage(c) {
            return c.messages[c.messages.length - 2];
        },
        getLastMessage(c) {
            return c.messages[c.messages.length - 1];
        },
        getChatHistory() {
            this.loading = true;
            fetchChatHistory(this.site._id, this.visitorId, this.page)
                .then((res) => {
                    log("fetchChatHistory", res.data);
                    const { conversations, totalCount, currentPage, pageSize } = res.data;
                    this.chats = [...this.chats, ...conversations];
                    this.loadMore = Math.ceil(totalCount / pageSize) !== currentPage;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        messageText(msg) {
            let escaped = htmlUnescape(msg.trim());

            let res = Autolinker.link(escaped, {
                className: "chatLink",
                truncate: { length: 50, location: "smart" },
            });

            res = res.replace(/<p>&nbsp;<\/p>$/gi, "");
            return res;
        },
        selectChat(c) {
            log("selectChat", c);
            this.selectedConversationId = c._id;
            this.loadOldMessages();
        },
        loadOldMessages() {
            log("loadOldMessages", this.selectedConversationId);
            this.loading = true;
            const convertMessages = (items) => {
                const allMessages = items
                    .map((x, i) => {
                        let type = "";
                        let author = "";
                        if (x.messageType === "system") {
                            type = "system";
                        } else if (["user", "chatbot"].includes(x.messageType)) {
                            type = "text";
                            author = x.userId || "Chatbot";
                        } else if (x.messageType.includes("chatbot")) {
                            type = x.messageType;
                            author = "Chatbot";
                        } else if (x.messageType === "private") {
                            type = "private";
                        } else if (x.messageType === "visitor") {
                            type = "text";
                            author = "me";
                        } else if (x.messageType === "user-media" || x.messageType === "visitor-media") {
                            author = x.messageType === "user-media" ? x.userId : "me";
                            if (
                                (!!x.asset.mime && x.asset.mime.includes("image")) ||
                                ["jpg", "jpeg", "png"].includes(x.asset.ext)
                            ) {
                                type = "image";
                            } else if (
                                (!!x.asset.mime && x.asset.mime.includes("audio")) ||
                                x.asset.ext.includes("mp3")
                            ) {
                                type = "audio";
                            } else {
                                type = "asset";
                            }
                        } else if (x.messageType === "visitor-location") {
                            author = x.messageType === "visitor-location" ? "me" : x.userId;
                            type = "location";
                        } else if (x.messageType === "user-location") {
                            author = x.messageType === "user-location" ? x.userId : "me";
                            type = "location";
                        } else if (x.messageType.startsWith("chatbot")) {
                            type = x.messageType;
                            author = "chatbot";
                        }
                        return {
                            type,
                            author,
                            data: {
                                text: x.message || x.description || _.get(x, "payload.text"),
                                meta: DateUtils.formatHour(x.createdDate),
                            },
                            raw: x,
                        };
                    })
                    .filter((x) => x !== undefined);
                log("allMessages", allMessages);
                return allMessages;
            };
            if (this.selectedConversationId);
            {
                return request({
                    url: `/conversations/${this.selectedConversationId}/messages`,
                    method: "get",
                })
                    .then((response) => {
                        this.messages = [];
                        let items = response.data.data;
                        items = _.orderBy(
                            _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                            (q) => new Date(q.createdDate).getTime(),
                            "asc"
                        );
                        log("messages ->", items);
                        this.messages = convertMessages(items);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },
    },
    mounted() {
        DateUtils.updateLocale(this.$i18n.locale);
        if (this.site && this.visitorId) this.getChatHistory();
    },
    updated() {
        if (DateUtils.updateLocale() !== this.$i18n.locale) DateUtils.updateLocale(this.$i18n.locale);
    },
    watch: {
        site(val) {
            this.getChatHistory();
        },
        visitorId(val) {
            this.getChatHistory();
        },
        page(val) {
            this.getChatHistory();
        },
    },
};
</script>
<style lang="scss">
body.v2 {
    #chat-history {
        h3,
        h4 {
            color: var(--color4) !important;
        }

        .ant-button {
            border-color: var(--color4) !important;
            color: var(--color4) !important;
        }

        #history-list {
            color: var(--color7) !important;
            display: flex;
            flex-direction: column;

            //border: 1px solid white;

            .item {
                width: 100%;
                display: flex;
                border-bottom: 1px solid var(--color7);
                margin-top: 5px;
                padding: 5px;
                cursor: pointer;
                align-items: center;

                .content {
                    padding-left: 4px;
                }

                &:hover {
                    border-right: 12px solid var(--color7);
                }

                .time {
                    font-size: 8px;
                }

                .messages {
                    span,
                    p {
                        font-size: 11px;
                        color: var(--color7);
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2; /* number of lines to show */
                        -webkit-box-orient: vertical;
                    }

                    span {
                        -webkit-line-clamp: 1;
                    }
                }
            }
        }
    }
}

body.v1 {
    #chat-history {
        .ant-skeleton-active {
            .ant-skeleton-title,
            .ant-skeleton-paragraph > li {
                background: linear-gradient(90deg, var(--color4) 25%, var(--color2) 37%, var(--color1) 63%);
                background-size: 400% 100%;
                -webkit-animation: ant-skeleton-loading 1.4s ease infinite;
                animation: ant-skeleton-loading 1.4s ease infinite;
            }

            .ant-skeleton-avatar {
                background: linear-gradient(90deg, var(--color4) 25%, var(--color2) 37%, var(--color1) 63%);
                background-size: 400% 100%;
                -webkit-animation: ant-skeleton-loading 1.4s ease infinite;
                animation: ant-skeleton-loading 1.4s ease infinite;
            }
        }

        .ant-result-title {
            color: var(--color4) !important;
        }
    }
}
</style>
<style></style>



// WEBPACK FOOTER //
// src/components/V2/ChatHistoryList.vue